import { RefObject, createRef, useCallback, useEffect, useRef } from "react";
import { Dimensions, View as RNView } from "react-native";

import { useAnalytics, useEventContext } from "@/hooks/analytics";

const trackSection = (
  sectionName: string,
  pathname: string,
  location: string,
  track: Function,
) => {
  track(`superclinical-${location}__section-scroll-${sectionName}`, {
    event_context: {
      pathname,
      location,
    },
  });
};

export default function useRefsToTrackScrollProgress(
  sectionNames: string[],
  preexistingRefs: Record<string, RefObject<RNView | null>>,
) {
  const sectionRefs = useRef<Record<string, RefObject<RNView | null>>>(
    sectionNames.reduce(
      (acc, sectionName) => {
        acc[sectionName] = preexistingRefs[sectionName] || createRef<RNView>();
        return acc;
      },
      {} as Record<string, RefObject<RNView | null>>,
    ),
  );
  const trackedSections = useRef<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { location, pathname } = useEventContext();
  const { track } = useAnalytics();

  const checkVisibility = useCallback(() => {
    // If all sections tracked, stop polling
    if (trackedSections.current.size === sectionNames.length) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    sectionNames.forEach((sectionName) => {
      const ref = sectionRefs.current[sectionName];
      if (!ref.current || trackedSections.current.has(sectionName)) return;

      ref.current.measureInWindow(
        (x: number, y: number, width: number, height: number) => {
          const windowHeight = Dimensions.get("window").height;

          // Check if top of element is in viewport
          if (y >= 0 && y <= windowHeight) {
            trackedSections.current.add(sectionName);
            trackSection(sectionName, pathname, location, track);
          }
        },
      );
    });
  }, [location, pathname, track, sectionNames]);

  useEffect(() => {
    checkVisibility();
    intervalRef.current = setInterval(checkVisibility, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkVisibility]);

  return sectionRefs.current;
}
